<template>
  <div>
    <p
      v-if="!isTransfer && doesNotHaveDeliveryDate"
      class="md-warning-text"
    >{{ value.expectedDeliveryDate }}</p>
    <div class="md-layout md-gutter">
      <f-field label="Fecha de solicitud" :value="value.addDate" />
      <template v-if="!isTransfer">
        <div class="field">
          <div class="outlined-field-date">
            <md-field :class="validationClass('dateOccurred')">
              <label class="active date-label">
                <strong>Fecha de entrega</strong>
              </label>
              <datetime
                :disabled="
                  loading ||
                  !$can(
                    'can_reschedule_material_entries',
                    'PurchaseRequisition',
                    'purchaserequisition'
                  ) ||
                  hasAllPhysicalEntries || value.residentValidation
                "
                :min-datetime="today"
                input-style="border: none; outline:none; width: 100%"
                type="datetime"
                data-vv-name="dateOccurred"
                title="Fecha y hora de ocurrido"
                value-zone="America/Mexico_City"
                :phrases="{ ok: 'Ok', cancel: 'Cancelar' }"
                v-model="value.expectedDeliveryDate"
                @input="$emit('changeDeliveryDate', $event)"
              ></datetime>
            </md-field>
          </div>
        </div>
        <f-field label="Condiciones de pago" :value="value.paymentConditions" />
        <f-field label="Condiciones de envío" :value="value.sendConditions" />
      </template>
    </div>
    <md-table v-model="value.suppliesConsolidated" md-card v-bind="$attrs" v-on="$listeners">
      <md-table-toolbar>
        <div class="md-toolbar-section-start md-layout-item md-size-100">
          <h1 class="md-title">Materiales</h1>
        </div>
      </md-table-toolbar>

      <md-table-row slot="md-table-row" slot-scope="{ item }">
        <md-table-cell md-label="Nombre" style="width: 400px">{{ item.name }}</md-table-cell>
        <md-table-cell md-label="Unidad de medida" style="width: 100px">{{ item.unitMeasurement }}</md-table-cell>
        <md-table-cell
          md-label="Precio por unidad"
          style="width: 100px"
        >{{ item.unitPrice | money }}</md-table-cell>
        <md-table-cell md-label="Precio total" style="width: 100px">{{ item.buyOrderPrice | money }}</md-table-cell>
        <md-table-cell md-label="Flete" style="width: 100px">{{ item.freightCost | money }}</md-table-cell>
        <md-table-cell md-label="Total con flete" style="width: 100px">{{ item.total | money }}</md-table-cell>
        <md-table-cell md-label="Cantidad" style="width: 100px">{{ item.quantity }}</md-table-cell>
        <md-table-cell md-label="Para almacén" style="width: 100px">
          <strong>{{ item.totalEntries }}</strong>
        </md-table-cell>
        <md-table-cell
          v-if="partialMode && hasNoPhysicalEntries"
          md-label="Cantidad recibida"
          style="width: 120px"
        >
          <div class="outlined-field-dense" style="display: inline-flex">
            <md-field>
              <md-input
                v-model="item.realReceivedAmount"
                type="Number"
                step="0.1"
                :disabled="runOutOfEntries(item)"
              ></md-input>
              <span class="md-suffix">
                <span
                  v-show="item.realReceivedAmount > item.quantity"
                  style="color: red"
                >max.{{ item.quantity }}</span>
              </span>
            </md-field>
            <md-icon v-if="runOutOfEntries(item)" class="tooltip-h">{{ "report_problem" }}</md-icon>
          </div>
          <md-tooltip v-if="runOutOfEntries(item)" md-direction="bottom">
            <p>Ya no se pueden hacer más entradas</p>
          </md-tooltip>
        </md-table-cell>
        <md-table-cell
          md-label="Recibido"
          style="width: 120px"
          v-else-if="!hasNoPhysicalEntries && !hasAllPhysicalEntries"
        >
          <div v-if="item.quantityPhysical < +item.quantity" class="outlined-field-dense">
            <md-field>
              <md-input
                required
                min="0"
                :max="item.quantity - item.totalEntries"
                v-model="item.receivedQuantity"
                type="number"
                step="0.00001"
              ></md-input>
              <span class="md-suffix">
                <span
                  v-if="
                    +item.receivedQuantity + item.quantityPhysical >
                    item.quantity
                  "
                  style="color: red"
                >max.{{ item.quantity - item.quantityPhysical }}</span>
              </span>
            </md-field>
          </div>
        </md-table-cell>
      </md-table-row>
    </md-table>
  </div>
</template>

<script>
import moment from "moment";
import { FField } from "@/components";
import usesAccountInfoMixin from "@/mixins/usesAccountInfo";

import { mapPermissions } from "@/plugins/accountPermissions/";

export default {
  name: "PurchaseOrderCard",
  components: { FField },
  mixins: [usesAccountInfoMixin],
  props: {
    value: {
      type: Object,
      require: true,
      default: () => ({}),
    },
    isTransfer: {
      type: Boolean,
      require: true,
      default: () => false,
    },
    hasAllPhysicalEntries: {
      type: Boolean,
      require: true,
      default: () => false,
    },
    partialMode: {
      type: Boolean,
      require: true,
      default: () => false,
    },
    hasNoPhysicalEntries: {
      type: Boolean,
      require: true,
      default: () => false,
    },
    loading: {
      type: Boolean,
      require: true,
      default: () => false,
    },
  },
  data() {
    const today = new Date(Date.now());
    return {
      today: today.toISOString(),
    };
  },
  computed: {
    ...mapPermissions("Materials_Quotation", "supplycamp"),
    ...mapPermissions("Materials_Quotation", "materialsquotation", ["add"]),
    ...mapPermissions("Materials_Quotation", "materialsquotationsupply", [
      "add",
    ]),
    ...mapPermissions("Materials_Quotation", "quotationrequisition", ["view"]),
    doesNotHaveDeliveryDate() {
      return !moment(this.value.expectedDeliveryDate).isValid();
    },
    disabled() {
      if (Object.keys(this.value).length !== 0) {
        const supplies = this.value.suppliesConsolidated.filter(
          (el) => el.receivedQuantity
        );
        return this.value.suppliesConsolidated.length !== supplies.length;
      }
      return true;
    },
  },
  methods: {
    runOutOfEntries({ quantityPhysical, totalEntries }) {
      return quantityPhysical === totalEntries;
    },
  },
};
</script>

<style lang="less" scoped>
.md-warning-text {
  color: orange;
}
.field {
  width: 25%;
  .outlined-field-date {
    max-width: 200px;
    height: 50px;
    .vdatetime {
      margin-top: 1.2em;
    }
    .picker-icon {
      top: 15px;
    }
    .date-label {
      top: -1px;
    }
  }
}
.md-card,
.md-card-content,
.md-card-actions {
  border: none !important;
  box-shadow: none;
}
.md-table + .md-table {
  margin-top: 16px;
}
</style>



// WEBPACK FOOTER //
// src/pages/Purchases/InputFromPurchaseOrder/PurchaseOrderCard.vue